import { differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { UrgencyBarConfig } from '@Components/CMS/Bar/BarFactory/Bars/Urgency/TallUrgencyBar/TallUrgencyBar'
import { getInfoData } from '@Components/CMS/Bar/BarFactory/Bars/Urgency/TallUrgencyBar/UrgencyBarHelpers'
import { ProgressBar } from '@Components/ProgressBar'

interface TimerProps {
  progress: number
  remainingHours: number
  remainingMinutes: number
  remainingSeconds: number
}

interface Props {
  className?: string
  config: UrgencyBarConfig
  fixed: number
  hourlyReset?: boolean
  progressColor?: string
  startAtFifty?: boolean
  timeUpdated?: (time: { hours: number, minutes: number, seconds: number }, expired: boolean) => void
}

const getFullTimer = (now, fromDate, toDate) => {
  const remainingHours = differenceInHours(toDate, now)
  const remainingMinutes = differenceInMinutes(toDate, now) % 60
  const remainingSeconds = differenceInSeconds(toDate, now) % 60
  const progress = ((now - fromDate) / (toDate - fromDate)) * 100
  return { remainingHours, remainingMinutes, remainingSeconds, progress }
}

const getHourlyTimer = (now, toDate) => {
  const hourReset: number = 1
  const remainingHours = differenceInHours(toDate, now) % hourReset
  const remainingMinutes = differenceInMinutes(toDate, now) % 60
  const remainingSeconds = differenceInSeconds(toDate, now) % 60
  const totalSeconds = hourReset * 60 * 60
  const remainingHoursInSeconds = remainingHours * 60 * 60
  const remainingMinutesInSeconds = remainingMinutes * 60
  const progress = ((totalSeconds - remainingHoursInSeconds - remainingMinutesInSeconds - remainingSeconds) / totalSeconds) * 100
  return { remainingHours, remainingMinutes, remainingSeconds, progress }
}

export const SlimProgressTimer = ({ className, config, fixed = 0, hourlyReset, progressColor, startAtFifty, timeUpdated }: Props) => {
  const intervalRef = useRef<ReturnType<typeof setInterval>>(null)
  const [percentage, setPercentage] = useState(100)
  const { range, type } = config
  const toDate = useMemo(() => range.to.getTime(), [range.to])
  const fromDate = useMemo(() => range.from.getTime(), [range.from])
  const { calculation = 'full' } = type ? getInfoData[type || ''] : ({ calculation: 'full' })

  const formatPercentage = useCallback((percent) => {
    const percentInverted = percent === 0 ? 0 : 100 - percent
    if (startAtFifty || calculation === 'partial') {
      const maxPercent = 50
      return Math.max(0, (maxPercent / 100) * percentInverted)
    }
    return Math.max(0, percentInverted)
  }, [calculation, startAtFifty])

  const calculateTimeRemaining = useCallback((now) => {
    let res: TimerProps
    if (hourlyReset) {
      res = getHourlyTimer(now, toDate)
    } else {
      res = getFullTimer(now, fromDate, toDate)
    }
    const { progress, remainingHours, remainingMinutes, remainingSeconds } = res
    return {
      remainingHours: Math.max(0, remainingHours),
      remainingMinutes: Math.max(0, remainingMinutes),
      remainingSeconds: Math.max(0, remainingSeconds),
      progress: Number.isFinite(progress) ? formatPercentage(progress) : 0,
    }
  }, [hourlyReset, formatPercentage, toDate, fromDate])

  const updateTimer = useCallback(() => {
    const now = new Date().getTime()
    if (now > toDate) {
      setPercentage(0)
      if (timeUpdated) {
        timeUpdated({ hours: 0, minutes: 0, seconds: 0 }, false)
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    const { progress, remainingHours, remainingMinutes, remainingSeconds } = calculateTimeRemaining(now)
    setPercentage(progress)
    if (timeUpdated) {
      timeUpdated(
        { hours: remainingHours, minutes: remainingMinutes, seconds: remainingSeconds },
        now > toDate,
      )
    }
  }, [calculateTimeRemaining, timeUpdated, toDate])

  useEffect(() => {
    updateTimer()
    intervalRef.current = setInterval(() => {
      updateTimer()
    }, 1000)

    return () => {
      if (!intervalRef.current) {
        return
      }
      clearInterval(intervalRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ProgressBar className={className} progressColor={progressColor} width={percentage.toFixed(fixed)} withMarginTop={false} />
  )
}
